import { Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useEffect, useState } from "react";

import { archiveCabinetInfoService } from "@/api/services/implementations";
import { liveUnitMetadataService } from "@/api/services/implementations/liveUnitMetadata";
import { useDispatch } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { ArchiveCabinetInfo, isArchiveCabinetInfo } from "@/utilities/types/archive";
import { PageType } from "@/utilities/types/common";
import { LiveUnitInfo, UnitType } from "@/utilities/types/liveunit";

import { useHeaderStyles } from "./HeaderStyles";


interface HeaderProps {
    unitType: UnitType
    pageType: PageType
    unitId: number
}


const Header: React.FC<HeaderProps> = ({ unitType, pageType, unitId }) => {
    const dispatch = useDispatch();
    const classes = useHeaderStyles();
    const [liveUnitInfo, setLiveUnitInfo] = useState<LiveUnitInfo | null>(null);
    const [archiveCabinetInfo, setArchiveCabinetInfo] = useState<ArchiveCabinetInfo | null>(null);

    useEffect(() => {
        // This is to ensure data fetched is not being set in useState when this component is unmounted.
        let isRunning = true;

        switch (unitType) {
            case UnitType.ArchiveCabinet:
                archiveCabinetInfoService
                    .fetchArchiveCabinetInfo(unitId)
                    .then((response) => {
                        if (isArchiveCabinetInfo(response)) {
                            if (isRunning) {
                                setArchiveCabinetInfo(response);
                            }
                        }
                    })
                    .catch(() => {
                        dispatch(
                            addAlert({
                                severity: Severity.Error,
                                message: "Failed to get Archive Cabinet information.",
                            }),
                        );
                    });
            break;
            case UnitType.LiveUnit:
                liveUnitMetadataService
                    .fetchLiveUnitInfo(unitId)
                    .then((response) => {
                        if (isRunning) {
                            setLiveUnitInfo(response);
                        }
                    })
                    .catch(() => {
                        dispatch(
                            addAlert({
                                severity: Severity.Error,
                                message: "Failed to get Live Unit information.",
                            }),
                        );
                    });
            break;
        }

        return () => { isRunning = false; };

    }, [unitType, unitId]);

    const lu_name = liveUnitInfo ? ((liveUnitInfo.liveUnitName !== "" && liveUnitInfo.liveUnitName !== liveUnitInfo.liveUnitSerialNum) ? `${liveUnitInfo.liveUnitName}, ` : "") + liveUnitInfo.liveUnitSerialNum : "";

    return (
        <>
            {(() => {
                switch (pageType) {
                    case PageType.Stream:
                        return (
                            liveUnitInfo ?
                                <div className={classes.head}>
                                    <Typography className={classes.locationName} variant="h5" noWrap >{liveUnitInfo.locationName}</Typography>
                                    {/* Show live unit name only if it exists and is not the same as the serial number (TDC)*/}
                                    <Typography className={classes.secondaryInfo} noWrap>{liveUnitInfo.showClientNameStreamPage && `${liveUnitInfo.clientName}   |   `}{lu_name}</Typography>
                                </div>
                                : <Skeleton variant="rect" className={classes.placeholder} />)
                            ;
                    case PageType.History:
                        switch (unitType) {
                            case UnitType.LiveUnit:
                                return (
                                    liveUnitInfo ?
                                        <div className={classes.head}>
                                            <Typography className={classes.locationName} variant="h5" noWrap>{liveUnitInfo.locationName}</Typography>
                                            {/* Show live unit name only if it exists and is not the same as the serial number (TDC)*/}
                                            <Typography className={classes.secondaryInfo} noWrap>{liveUnitInfo.showClientNameHistoryPage && `${liveUnitInfo.clientName}   |   `}{lu_name}</Typography>
                                        </div>
                                        : <Skeleton variant="rect" className={classes.placeholder} />
                                );
                            case UnitType.ArchiveCabinet:
                                return (
                                    archiveCabinetInfo ?
                                        <div className={classes.head}>
                                            <Typography className={classes.locationName} variant="h5" noWrap>{archiveCabinetInfo.locationName}</Typography>
                                            <Typography className={classes.secondaryInfo} noWrap>{archiveCabinetInfo.showClientName && `${archiveCabinetInfo.clientName}   |   `}{archiveCabinetInfo.liveUnitName}</Typography>
                                        </div>
                                        : <Skeleton variant="rect" className={classes.placeholder} />
                                );
                            default:
                                return null;
                        }
                    default:
                        return null;
                }
            })()}
        </>
    );
};

export default Header;
